import Icon from '@/components/ui/AppIcon';

interface Position {
  id: number;
  title: string;
  type: string;
  location: string;
  salary: string;
  description: string;
  requirements: string[];
  responsibilities: string[];
}

const positions: Position[] = [
  {
    id: 1,
    title: 'Agent de Nettoyage Résidentiel',
    type: 'Temps Plein / Temps Partiel',
    location: 'Le Locle et environs',
    salary: 'CHF 22-25/heure',
    description: 'Nous recherchons des agents de nettoyage motivés pour rejoindre notre équipe résidentielle. Vous serez responsable du nettoyage et de l\'entretien de maisons et appartements privés.',
    requirements: [
      'Expérience en nettoyage résidentiel (1+ an préféré)',
      'Attention aux détails et sens de l\'organisation',
      'Capacité à travailler de manière autonome',
      'Permis de conduire valide (préféré)',
      'Français ou allemand courant'
    ],
    responsibilities: [
      'Nettoyage complet des espaces résidentiels',
      'Utilisation appropriée des produits de nettoyage',
      'Respect des protocoles de sécurité',
      'Communication professionnelle avec les clients',
      'Gestion du temps et des priorités'
    ]
  },
  {
    id: 2,
    title: 'Technicien de Nettoyage Commercial',
    type: 'Temps Plein',
    location: 'Le Locle',
    salary: 'CHF 24-27/heure',
    description: 'Rejoignez notre équipe commerciale pour assurer le nettoyage et l\'entretien de bureaux, commerces et espaces professionnels dans la région de Le Locle.',
    requirements: [
      'Expérience en nettoyage commercial (2+ ans)',
      'Connaissance des équipements professionnels',
      'Flexibilité horaire (travail en soirée possible)',
      'Capacité à soulever jusqu\'à 20kg',
      'Certificat de travail de nuit (si applicable)'
    ],
    responsibilities: [
      'Nettoyage des bureaux et espaces commerciaux',
      'Entretien des sols et surfaces',
      'Gestion des déchets et recyclage',
      'Maintenance préventive des équipements',
      'Rapport quotidien des activités'
    ]
  },
  {
    id: 3,
    title: 'Superviseur d\'Équipe',
    type: 'Temps Plein',
    location: 'Le Locle',
    salary: 'CHF 26-28/heure',
    description: 'Nous cherchons un superviseur expérimenté pour coordonner nos équipes de nettoyage et assurer la qualité de nos services auprès de nos clients.',
    requirements: [
      'Expérience en supervision (3+ ans)',
      'Leadership et compétences managériales',
      'Excellentes compétences en communication',
      'Connaissance des standards de qualité suisses',
      'Bilingue français/allemand (atout majeur)'
    ],
    responsibilities: [
      'Coordination des équipes de nettoyage',
      'Contrôle qualité des prestations',
      'Formation et encadrement du personnel',
      'Gestion des plannings et ressources',
      'Relation client et résolution de problèmes'
    ]
  },
  {
    id: 4,
    title: 'Spécialiste Nettoyage Spécialisé',
    type: 'Temps Plein',
    location: 'Le Locle et région',
    salary: 'CHF 25-28/heure',
    description: 'Poste spécialisé pour technicien expérimenté en nettoyage après construction, nettoyage industriel et interventions techniques complexes.',
    requirements: [
      'Formation technique en nettoyage spécialisé',
      'Expérience avec équipements professionnels',
      'Connaissance des normes de sécurité',
      'Capacité à travailler en hauteur',
      'Permis de conduire obligatoire'
    ],
    responsibilities: [
      'Nettoyage après construction et rénovation',
      'Nettoyage industriel et technique',
      'Utilisation d\'équipements spécialisés',
      'Respect des protocoles de sécurité',
      'Documentation des interventions'
    ]
  }
];

interface OpenPositionsSectionProps {
  onApply: (positionId: number) => void;
}

const OpenPositionsSection = ({ onApply }: OpenPositionsSectionProps) => {
  return (
    <section className="py-16 lg:py-24 bg-surface">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-heading font-bold text-foreground mb-4">
            Postes Disponibles
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Découvrez nos opportunités actuelles et trouvez le poste qui correspond à vos compétences et aspirations professionnelles
          </p>
        </div>

        <div className="space-y-6 max-w-5xl mx-auto">
          {positions.map((position) => (
            <div
              key={position.id}
              className="bg-card rounded-lg shadow-sm border border-border overflow-hidden hover:shadow-md transition-shadow duration-300"
            >
              <div className="p-6 lg:p-8">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between mb-4">
                  <div className="mb-4 lg:mb-0">
                    <h3 className="text-2xl font-heading font-bold text-foreground mb-2">
                      {position.title}
                    </h3>
                    <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
                      <div className="flex items-center space-x-1">
                        <Icon name="BriefcaseIcon" size={16} variant="outline" />
                        <span>{position.type}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Icon name="MapPinIcon" size={16} variant="outline" />
                        <span>{position.location}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Icon name="CurrencyDollarIcon" size={16} variant="outline" />
                        <span className="font-semibold text-primary">{position.salary}</span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => onApply(position.id)}
                    className="bg-primary text-primary-foreground px-6 py-3 rounded-lg font-heading font-semibold hover:bg-primary/90 transition-colors duration-300 flex items-center space-x-2 whitespace-nowrap"
                  >
                    <span>Postuler</span>
                    <Icon name="ArrowRightIcon" size={20} variant="outline" />
                  </button>
                </div>

                <p className="text-foreground mb-6 leading-relaxed">
                  {position.description}
                </p>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-heading font-semibold text-foreground mb-3 flex items-center space-x-2">
                      <Icon name="CheckCircleIcon" size={20} className="text-success" />
                      <span>Exigences</span>
                    </h4>
                    <ul className="space-y-2">
                      {position.requirements.map((req, index) => (
                        <li key={index} className="text-sm text-muted-foreground flex items-start space-x-2">
                          <span className="text-primary mt-1">•</span>
                          <span>{req}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-heading font-semibold text-foreground mb-3 flex items-center space-x-2">
                      <Icon name="ClipboardDocumentListIcon" size={20} className="text-secondary" />
                      <span>Responsabilités</span>
                    </h4>
                    <ul className="space-y-2">
                      {position.responsibilities.map((resp, index) => (
                        <li key={index} className="text-sm text-muted-foreground flex items-start space-x-2">
                          <span className="text-primary mt-1">•</span>
                          <span>{resp}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default OpenPositionsSection;